// Bloques que comparten las tarjetas de «Elige el teléfono» (por SKU y por modelo): precio con su
// descuento y el enganche desde pegado, y la lista de promos. Solo `span` para caber en un botón.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import { cardCreditOf, type LevelCreditTerms } from '../cardBenefits';
import type { BenefitBubble, CardSaleBenefits } from '../promotionSlots';
import type { CatalogItem } from '../types';

/** Cuántas promos se listan antes del «+N más». */
export const CARD_PROMO_LIMIT = 4;

/** Una promo de la tarjeta; `amount` es el monto a la derecha («−$500.00», «+$200.00»). */
export type CardPromo = BenefitBubble & { amount?: string };

/** Color de icono y monto por tipo: verde descuento, azul regalo, amarillo bono, morado crédito. */
const PROMO_TONE: Record<NonNullable<BenefitBubble['tone']> | 'sale', string> = {
    sale: 'text-success',
    coupon: 'text-seg-slate',
    credit: 'text-promo',
    gift: 'text-info',
    benefit: 'text-warning',
};

/** Precio a cobrar: el con descuento si de verdad baja el de lista. */
export function cardPriceOf(
    listCents: number,
    saleBenefits: CardSaleBenefits,
): { priceCents: number; hasDiscount: boolean } {
    const discounted = saleBenefits.discountedPriceCents;
    const hasDiscount = discounted !== null && discounted < listCents;
    return { priceCents: hasDiscount ? discounted : listCents, hasDiscount };
}

/** «Enganche desde $X» con SureKeep confirmado y que lo financie; sin cotas, «Con SureKeep». */
function DownPaymentLine({
    item,
    priceCents,
    creditTerms,
}: {
    item: CatalogItem;
    priceCents: number;
    creditTerms: LevelCreditTerms;
}) {
    const credit = cardCreditOf(item, priceCents, creditTerms);
    if (credit.kind !== 'surekeep') return null;
    return (
        <span
            data-testid="down-payment"
            className="mt-1.5 text-[12.5px] leading-[1.25] text-muted-foreground"
        >
            {credit.downPayment ? (
                <>
                    {'Enganche desde '}
                    <strong className="font-semibold text-foreground tabular-nums">
                        {formatMXN(credit.downPayment.cents / 100)}
                    </strong>
                </>
            ) : (
                'Con SureKeep'
            )}
        </span>
    );
}

/** Precio tachado, precio a cobrar (con «desde» si aún no hay variante), contado y enganche. */
export function CardPrice({
    item,
    listCents,
    saleBenefits,
    creditTerms,
    from = false,
}: {
    /** El SKU del que sale el enganche. */
    item: CatalogItem;
    listCents: number;
    saleBenefits: CardSaleBenefits;
    creditTerms: LevelCreditTerms;
    /** El precio es el mínimo del modelo: se antepone «Desde». */
    from?: boolean;
}) {
    const { priceCents, hasDiscount } = cardPriceOf(listCents, saleBenefits);
    const cash =
        saleBenefits.cashPriceCents !== undefined
            ? `De contado ${formatMXN(saleBenefits.cashPriceCents / 100)}`
            : null;
    return (
        <>
            <span
                data-testid="original-price"
                className="mt-2.5 min-h-[17px] text-[12px] text-muted-foreground tabular-nums line-through"
            >
                {hasDiscount ? formatMXN(listCents / 100) : null}
            </span>
            <span
                data-testid="price"
                className={cn(
                    'flex items-baseline gap-1.5 text-[24px] font-bold leading-tight tabular-nums',
                    hasDiscount && 'text-success',
                )}
            >
                {from && (
                    <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                        Desde
                    </span>
                )}
                {formatMXN(priceCents / 100)}
            </span>
            {cash !== null && (
                <span
                    data-testid="cash-price"
                    className="text-[12px] text-muted-foreground tabular-nums"
                >
                    {cash}
                </span>
            )}
            <DownPaymentLine item={item} priceCents={priceCents} creditTerms={creditTerms} />
        </>
    );
}

function PromoLine({ promo }: { promo: CardPromo }) {
    const Icon = promo.icon;
    const tone = PROMO_TONE[promo.tone ?? 'sale'];
    return (
        <span
            title={promo.title ?? promo.label}
            className="flex min-w-0 items-center gap-1.5 text-[12.5px] leading-[1.4]"
        >
            <Icon className={cn('size-3.5 flex-none', tone)} aria-hidden />
            <span className="min-w-0 flex-1 truncate">{promo.label}</span>
            {promo.amount !== undefined && (
                <span className={cn('flex-none font-semibold tabular-nums', tone)}>
                    {promo.amount}
                </span>
            )}
        </span>
    );
}

/** Lista de promos al pie: hasta 4 y «+N más»; sin promos no se pinta. */
export function CardPromoList({ promos }: { promos: readonly CardPromo[] }) {
    if (promos.length === 0) return null;
    const shown = promos.slice(0, CARD_PROMO_LIMIT);
    const rest = promos.length - shown.length;
    return (
        <span
            data-section="promos"
            className="flex flex-col gap-0.5 border-t border-border/60 px-4 py-2.5"
        >
            {shown.map((promo) => (
                <PromoLine key={promo.key} promo={promo} />
            ))}
            {rest > 0 && (
                <span className="text-[12px] font-semibold text-muted-foreground">+{rest} más</span>
            )}
        </span>
    );
}

/** Promos de la tarjeta: las que manda quien monta o, sin ellas, las de venta y luego las de crédito. */
export function cardPromosOf(
    promos: readonly CardPromo[] | undefined,
    saleBenefits: CardSaleBenefits,
    creditBubbles: readonly BenefitBubble[],
): readonly CardPromo[] {
    return promos ?? [...saleBenefits.bubbles, ...creditBubbles];
}

/** Pie de la tarjeta con su acción a la derecha. */
export function CardActions({ children }: { children: ReactNode }) {
    return (
        <span
            data-section="actions"
            className="flex items-center justify-end gap-2 border-t border-border/60 px-4 py-2.5"
        >
            {children}
        </span>
    );
}
